// Background outbox for outbound email (replies and new-ticket compose).
//
// Pure: no React, no Supabase, no toasts. Everything the UI needs is exposed
// through `subscribe` / `getSnapshot` (shaped for useSyncExternalStore) and the
// action methods below.
//
// ── Why it lives outside any component ──────────────────────────────────────
// Sending used to be owned by the composer: it held the undo timer, the
// mutation, and the "Sending…" button. So the composer had to stay mounted for
// the whole send — and since `App.tsx` re-mounts the routed subtree on every
// navigation, leaving the page meant cutting the undo window short and losing
// Undo. Here the timer and the send belong to a module-level store, so Send can
// return at once and the person can go anywhere.
//
// ── Job lifecycle ───────────────────────────────────────────────────────────
//   queued ──(timer / sendNow)──▶ sending ──▶ sent ──▶ (removed)
//      │                             └──▶ failed ──(retry)──▶ sending
//      └──(undo)──▶ held ──(sendNow)──▶ sending
//
//  * `queued`  inside the undo window (`sendAt` says when it ends).
//  * `held`    the person pressed Undo. Nothing is sent; the message waits to
//              be edited or sent.
//  * `sending` handed to the network. Cannot be undone any more. While it runs,
//              the job may report a `stage` ("Uploading 1 of 2 files…") so the
//              UI can say what it is doing instead of a bare spinner.
//  * `sent`    delivered; the job lingers only until `onSuccess` has finished
//              (the thread refetch), so the real message replaces the bubble
//              with no gap. It is no longer "unsent work".
//  * `failed`  the error is kept; Retry reuses the SAME payload, so the same
//              idempotency key — if the first attempt did reach Gmail, the
//              workflow answers `deduped` instead of sending twice.
//
// Jobs with the same `key` (a ticket id, or 'compose') go out one after another
// in the order they were queued, so two quick replies land in order. Different
// keys go in parallel.

/** How long a reply waits (and can be undone) before it is sent. */
export const UNDO_DELAY_MS = 5000;

/** Upper bound on how long a delivered job lingers waiting for `onSuccess`
 *  (the thread refetch). After this the bubble goes regardless. */
export const SENT_LINGER_MAX_MS = 10_000;

export type OutboxState = 'queued' | 'held' | 'sending' | 'sent' | 'failed';

export interface OutboxJobView<P = unknown> {
  readonly id: string;
  /** Ordering lane: a ticket id for replies, 'compose' for new tickets. */
  readonly key: string;
  readonly kind: string;
  /** Short human label for toasts / bubbles, e.g. the ticket subject. */
  readonly title: string;
  readonly state: OutboxState;
  /** Epoch ms the undo window ends. Only set while `queued`. */
  readonly sendAt: number | null;
  /** 0 until the first send starts, then 1, 2… (one per retry). */
  readonly attempt: number;
  /** Message of the last failure. Only set while `failed`. */
  readonly error: string | null;
  /** What the send is doing right now ("Uploading 1 of 2 files…"), as reported
   *  by its `run`. null = nothing specific. Only set while `sending`. */
  readonly stage: string | null;
  readonly createdAt: number;
  readonly payload: P;
}

/** What `run` is given besides the payload. */
export interface RunInfo {
  /** 1 for the first send, 2 for the first retry… */
  attempt: number;
  /** Say what the send is doing ("Uploading 1 of 2 files…"); null clears it.
   *  Ignored once this attempt is over, so a late call from a failed attempt
   *  can never write onto its retry. */
  report: (stage: string | null) => void;
}

export interface EnqueueInput<P, R = unknown> {
  key: string;
  kind: string;
  title: string;
  /** The snapshot the send works from. Owned by the caller; kept as-is so a
   *  retry (or taking the job back for editing) sees exactly what was sent. */
  payload: P;
  /** The actual send. Must work from `payload` alone — the composer that queued
   *  it may be long gone. Throw / reject to fail the job. */
  run: (payload: P, info: RunInfo) => Promise<R>;
  /** Runs once after a successful send. Its promise holds the job as `sent`
   *  (capped by SENT_LINGER_MAX_MS). Errors are logged, never turn a delivered
   *  email into a failure. */
  onSuccess?: (result: R, job: OutboxJobView<P>) => void | Promise<void>;
  /** Runs after each failed attempt. */
  onFailure?: (error: unknown, job: OutboxJobView<P>) => void;
  /** Length of the undo window. 0 sends immediately. Default UNDO_DELAY_MS. */
  delayMs?: number;
}

export interface Outbox {
  enqueue<P, R = unknown>(input: EnqueueInput<P, R>): string;
  /** queued → held. False when it is no longer inside the window. */
  undo(id: string): boolean;
  /** queued | held | failed → sending now. A queued job also releases the
   *  earlier queued jobs of its lane first, so order is preserved. */
  sendNow(id: string): boolean;
  /** failed → sending again, same payload. */
  retry(id: string): boolean;
  /** Drop a queued / held / failed job. False once it is sending or sent. */
  discard(id: string): boolean;
  /** discard(), but hands the job back so the composer can take it. */
  take<P = unknown>(id: string): OutboxJobView<P> | null;
  get<P = unknown>(id: string): OutboxJobView<P> | undefined;
  jobsFor(key: string): OutboxJobView[];
  all(): OutboxJobView[];
  /** True while any job is queued, held, sending or failed. Drives the
   *  "leave site?" prompt — the outbox is memory-only. */
  hasUnsentWork(): boolean;
  subscribe(listener: () => void): () => void;
  /** Stable between changes (a new array only after a change). */
  getSnapshot(): readonly OutboxJobView[];
}

interface InternalJob {
  id: string;
  key: string;
  kind: string;
  title: string;
  state: OutboxState;
  sendAt: number | null;
  attempt: number;
  error: string | null;
  stage: string | null;
  createdAt: number;
  seq: number;
  payload: unknown;
  run: (payload: unknown, info: RunInfo) => Promise<unknown>;
  onSuccess?: (result: unknown, job: OutboxJobView) => void | Promise<void>;
  onFailure?: (error: unknown, job: OutboxJobView) => void;
  timer: ReturnType<typeof setTimeout> | null;
}

/** Whole seconds left in the undo window, for a "Sending in 4s" label. */
export function undoSecondsLeft(job: { sendAt: number | null }, now: number): number {
  if (job.sendAt === null) return 0;
  return Math.max(0, Math.ceil((job.sendAt - now) / 1000));
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export function createOutbox(): Outbox {
  const jobs = new Map<string, InternalJob>();
  const listeners = new Set<() => void>();
  // One lane per key: at most one job running, the rest waiting in order.
  const lanes = new Map<string, { running: boolean; waiting: string[] }>();
  let seq = 0;
  let snapshot: readonly OutboxJobView[] = [];
  let dirty = false;

  const view = (j: InternalJob): OutboxJobView => ({
    id: j.id,
    key: j.key,
    kind: j.kind,
    title: j.title,
    state: j.state,
    sendAt: j.sendAt,
    attempt: j.attempt,
    error: j.error,
    stage: j.stage,
    createdAt: j.createdAt,
    payload: j.payload,
  });

  const ordered = (): InternalJob[] => [...jobs.values()].sort((a, b) => a.seq - b.seq);

  function notify() {
    dirty = true;
    for (const l of [...listeners]) l();
  }

  function clearTimer(j: InternalJob) {
    if (j.timer !== null) {
      clearTimeout(j.timer);
      j.timer = null;
    }
  }

  function remove(j: InternalJob) {
    clearTimer(j);
    jobs.delete(j.id);
    notify();
  }

  function pump(key: string) {
    const lane = lanes.get(key);
    if (!lane || lane.running) return;
    while (lane.waiting.length > 0) {
      const next = jobs.get(lane.waiting.shift()!);
      if (next && next.state === 'sending') {
        lane.running = true;
        void execute(next).finally(() => {
          lane.running = false;
          pump(key);
        });
        return;
      }
    }
  }

  /** Hand a queued / held / failed job to its lane. */
  function dispatch(j: InternalJob) {
    clearTimer(j);
    j.state = 'sending';
    j.sendAt = null;
    j.error = null;
    j.stage = null;
    j.attempt += 1;
    notify();
    let lane = lanes.get(j.key);
    if (!lane) {
      lane = { running: false, waiting: [] };
      lanes.set(j.key, lane);
    }
    lane.waiting.push(j.id);
    pump(j.key);
  }

  async function execute(j: InternalJob): Promise<void> {
    const attempt = j.attempt;
    const report = (stage: string | null) => {
      // Only the attempt that is running may speak.
      if (j.state !== 'sending' || j.attempt !== attempt || j.stage === stage) return;
      j.stage = stage;
      notify();
    };

    let result: unknown;
    try {
      result = await j.run(j.payload, { attempt, report });
    } catch (err) {
      j.state = 'failed';
      j.error = messageOf(err);
      j.stage = null;
      notify();
      try {
        j.onFailure?.(err, view(j));
      } catch (e) {
        console.warn('outbox onFailure threw', e);
      }
      return;
    }

    j.state = 'sent';
    j.stage = null;
    notify();
    // The lane is released as soon as this function returns; the linger below
    // runs on its own so a slow refetch never delays the next send.
    void finishSent(j, result);
  }

  async function finishSent(j: InternalJob, result: unknown): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const done = Promise.resolve(j.onSuccess?.(result, view(j)));
      const cap = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, SENT_LINGER_MAX_MS);
      });
      await Promise.race([done, cap]);
    } catch (e) {
      console.warn('outbox onSuccess threw', e);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      if (jobs.get(j.id) === j) remove(j);
    }
  }

  const editable = (j: InternalJob | undefined): j is InternalJob =>
    !!j && (j.state === 'queued' || j.state === 'held' || j.state === 'failed');

  return {
    enqueue<P, R = unknown>(input: EnqueueInput<P, R>): string {
      const id = `outbox-${++seq}`;
      const delayMs = input.delayMs ?? UNDO_DELAY_MS;
      const job: InternalJob = {
        id,
        key: input.key,
        kind: input.kind,
        title: input.title,
        state: 'queued',
        sendAt: Date.now() + delayMs,
        attempt: 0,
        error: null,
        stage: null,
        createdAt: Date.now(),
        seq,
        payload: input.payload,
        run: input.run as InternalJob['run'],
        onSuccess: input.onSuccess as InternalJob['onSuccess'],
        onFailure: input.onFailure as InternalJob['onFailure'],
        timer: null,
      };
      jobs.set(id, job);

      if (delayMs <= 0) {
        dispatch(job);
      } else {
        job.timer = setTimeout(() => {
          job.timer = null;
          if (job.state === 'queued') dispatch(job);
        }, delayMs);
        notify();
      }
      return id;
    },

    undo(id) {
      const j = jobs.get(id);
      if (!j || j.state !== 'queued') return false;
      clearTimer(j);
      j.state = 'held';
      j.sendAt = null;
      notify();
      return true;
    },

    sendNow(id) {
      const j = jobs.get(id);
      if (!editable(j)) return false;
      if (j.state === 'queued') {
        // Keep the thread in order: anything queued earlier for this ticket
        // goes first. Jobs the person undid (held) stay held.
        for (const earlier of ordered()) {
          if (earlier.seq >= j.seq) break;
          if (earlier.key === j.key && earlier.state === 'queued') dispatch(earlier);
        }
      }
      dispatch(j);
      return true;
    },

    retry(id) {
      const j = jobs.get(id);
      if (!j || j.state !== 'failed') return false;
      dispatch(j);
      return true;
    },

    discard(id) {
      const j = jobs.get(id);
      if (!editable(j)) return false;
      remove(j);
      return true;
    },

    take<P = unknown>(id: string): OutboxJobView<P> | null {
      const j = jobs.get(id);
      if (!editable(j)) return null;
      const out = view(j) as OutboxJobView<P>;
      remove(j);
      return out;
    },

    get<P = unknown>(id: string): OutboxJobView<P> | undefined {
      const j = jobs.get(id);
      return j ? (view(j) as OutboxJobView<P>) : undefined;
    },

    jobsFor(key) {
      return ordered()
        .filter((j) => j.key === key)
        .map(view);
    },

    all() {
      return ordered().map(view);
    },

    hasUnsentWork() {
      for (const j of jobs.values()) {
        if (j.state !== 'sent') return true;
      }
      return false;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot() {
      if (dirty) {
        snapshot = ordered().map(view);
        dirty = false;
      }
      return snapshot;
    },
  };
}

/** The app-wide outbox. Composers, the thread view, the toasts and the
 *  tab-close guard all talk to this one instance. */
export const outbox: Outbox = createOutbox();
